/**
 * AndromedApp — Dashboard operativo de la réplica de CRM_FARMACIA (schema `crm`
 * en Supabase). Organizado por áreas de enfoque con filtro de fecha global:
 *
 *   Ventas Totales · PDV · Cadenas / Geolocalización · Consejera · Producto
 *
 * Backend: routes/crm.py · Servicio: services/crmService.js
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'leaflet/dist/leaflet.css';
import {
  FaCapsules, FaSyncAlt, FaShoppingCart, FaBoxes, FaReceipt, FaTag,
  FaStore, FaMapMarkerAlt, FaUserNurse, FaLayerGroup, FaCalendarAlt,
  FaExclamationTriangle, FaCheckCircle, FaClock, FaFileMedical, FaBolt, FaChartLine,
  FaChartBar,
} from 'react-icons/fa';
import {
  getCrmBounds, getCrmOverview, getCrmSalesTimeseries,
  getCrmPdv, getCrmGeo, getCrmConsejeras, getCrmProductos,
} from '../../services/crmService';
import './CrmPage.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Tooltip, Legend, Filler);

// ── Paleta de marca ──────────────────────────────────────────────────────────
const C = {
  primary: '#073C5C', accent: '#F68D2E',
  series: ['#073C5C', '#F68D2E', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899',
    '#f59e0b', '#14b8a6', '#ef4444', '#64748b'],
};

// ── Formateadores ────────────────────────────────────────────────────────────
const numFmt = new Intl.NumberFormat('es-MX');
const money = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(Number(n) || 0);
const moneyC = (n) => '$' + new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n) || 0);
const compact = (n) => new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n) || 0);
const num = (n) => numFmt.format(Number(n) || 0);
const pct1 = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const fecha = (s) => (s ? new Date(s).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const fmtBucket = (b) => {
  if (!b) return '';
  if (b.length === 10) return `${b.slice(8, 10)}/${b.slice(5, 7)}`;      // YYYY-MM-DD → DD/MM
  if (b.length === 7) return `${b.slice(5, 7)}/${b.slice(2, 4)}`;        // YYYY-MM → MM/YY
  return b;
};
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const mesIdx = (ym) => Number(ym.slice(5, 7)) - 1;                       // YYYY-MM → 0..11
const fmtMes = (ym) => `${MESES[mesIdx(ym)] || '?'} ${ym.slice(2, 4)}`;           // → ene 24
const fmtMesLargo = (ym) => `${MESES_LARGO[mesIdx(ym)] || '?'} ${ym.slice(0, 4)}`; // → enero 2024

// ── Utilidades de fecha (rango local, sin desfase de zona horaria) ────────────
const isoOf = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// ── Presets de fecha construidos a partir de la última venta ──────────────────
function buildPresets(bounds) {
  if (!bounds?.max_date) return [];
  const max = parseISO(bounds.max_date);
  const min = bounds.min_date ? parseISO(bounds.min_date) : addDays(max, -365);
  const clamp = (d) => (d < min ? min : d);
  const range = (fromDate) => ({ from: isoOf(clamp(fromDate)), to: isoOf(max) });
  return [
    { key: '7d', label: '7 días', range: range(addDays(max, -6)) },
    { key: '30d', label: '30 días', range: range(addDays(max, -29)) },
    { key: '90d', label: '90 días', range: range(addDays(max, -89)) },
    { key: '12m', label: '12 meses', range: range(addDays(max, -364)) },
    { key: 'ytd', label: `${max.getFullYear()}`, range: range(new Date(max.getFullYear(), 0, 1)) },
    { key: 'all', label: 'Todo', range: { from: isoOf(min), to: isoOf(max) } },
  ];
}

// ── Serie mensual de ventas ───────────────────────────────────────────────────
/**
 * Agrupa `ts.series` por mes calendario. NO pide nada al backend: cuando la
 * granularidad es 'day' los buckets diarios cubren el rango COMPLETO (crm.py no
 * los recorta ni los pagina), así que sumarlos por mes da exactamente el mismo
 * número que volver a agregar en Athena, sin una query extra ni MB escaneados.
 * Cuando la granularidad ya es 'month' el rollup es la identidad.
 */
function monthlySales(series, range) {
  const acc = new Map();
  (series || []).forEach((s) => {
    const ym = String(s.bucket || '').slice(0, 7);   // 'YYYY-MM-DD' y 'YYYY-MM' → 'YYYY-MM'
    if (ym.length !== 7) return;
    const a = acc.get(ym) || { valor: 0, unidades: 0, tickets: 0 };
    a.valor += Number(s.valor) || 0;
    a.unidades += Number(s.unidades) || 0;
    a.tickets += Number(s.tickets) || 0;
    acc.set(ym, a);
  });

  // Los meses salen del RANGO, no de los datos: un mes sin ventas tiene que
  // aparecer como barra en cero, no desaparecer del eje corriendo a las demás.
  let keys = [];
  if (range?.from && range?.to) {
    let [y, m] = range.from.split('-').map(Number);
    const [ty, tm] = range.to.split('-').map(Number);
    while ((y < ty || (y === ty && m <= tm)) && keys.length < 600) {
      keys.push(`${y}-${String(m).padStart(2, '0')}`);
      m += 1;
      if (m === 13) { y += 1; m = 1; }
    }
  }
  if (!keys.length) keys = [...acc.keys()].sort();

  // El primer y el último mes pueden venir recortados por el filtro (los presets
  // arrancan en un día cualquiera y `to` es la última venta cargada). Esas barras
  // no son comparables con las completas: se marcan para atenuarlas y excluirlas
  // del promedio y del delta mes a mes, en vez de leerse como una caída real.
  const primero = range?.from && Number(range.from.slice(8, 10)) !== 1
    ? range.from.slice(0, 7) : null;
  const ultimoDia = range?.to
    ? new Date(Number(range.to.slice(0, 4)), Number(range.to.slice(5, 7)), 0).getDate() : null;
  const ultimo = range?.to && Number(range.to.slice(8, 10)) !== ultimoDia
    ? range.to.slice(0, 7) : null;

  return keys.map((ym) => ({
    mes: ym,
    parcial: ym === primero || ym === ultimo,
    ...(acc.get(ym) || { valor: 0, unidades: 0, tickets: 0 }),
  }));
}

const TABS = [
  { key: 'ventas', label: 'Ventas Totales', icon: <FaShoppingCart /> },
  { key: 'pdv', label: 'PDV', icon: <FaStore /> },
  { key: 'geo', label: 'Cadenas / Geo', icon: <FaMapMarkerAlt /> },
  { key: 'consejeras', label: 'Consejera', icon: <FaUserNurse /> },
  { key: 'productos', label: 'Producto', icon: <FaTag /> },
];

// ═══════════════════════════════════════════════════════════════════════════
export default function CrmPage() {
  const [bounds, setBounds] = useState(null);
  const [range, setRange] = useState(null);
  const [activeTab, setActiveTab] = useState('ventas');
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState(null);

  const [cache, setCache] = useState({});     // { tab: data }
  const [loading, setLoading] = useState({}); // { tab: bool }
  const [errors, setErrors] = useState({});   // { tab: msg }
  const rangeToken = useRef(0);               // se incrementa al cambiar el rango (invalida respuestas viejas)

  const presets = useMemo(() => buildPresets(bounds), [bounds]);
  const activePreset = useMemo(
    () => presets.find((p) => range && p.range.from === range.from && p.range.to === range.to)?.key || 'custom',
    [presets, range],
  );

  // Arranque: límites de fecha → rango por defecto (30 días)
  useEffect(() => {
    (async () => {
      try {
        const b = await getCrmBounds();
        setBounds(b.bounds);
        const ps = buildPresets(b.bounds);
        setRange(ps.find((p) => p.key === '30d')?.range || ps[ps.length - 1]?.range || null);
      } catch (e) {
        setBootError(e.message || 'No se pudo inicializar AndromedApp');
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  const fetchTab = useCallback(async (tab, rng) => {
    switch (tab) {
      case 'ventas': {
        const [overview, ts] = await Promise.all([
          getCrmOverview(rng), getCrmSalesTimeseries(rng),
        ]);
        return { overview, ts };
      }
      case 'pdv': return getCrmPdv(rng);
      case 'geo': return getCrmGeo(rng);
      case 'consejeras': return getCrmConsejeras(rng);
      case 'productos': return getCrmProductos(rng);
      default: return null;
    }
  }, []);

  const loadTab = useCallback(async (tab, rng) => {
    const token = rangeToken.current;
    setLoading((s) => ({ ...s, [tab]: true }));
    setErrors((s) => ({ ...s, [tab]: null }));
    try {
      const data = await fetchTab(tab, rng);
      if (token !== rangeToken.current) return;   // el rango cambió mientras cargaba
      setCache((s) => ({ ...s, [tab]: data }));
    } catch (e) {
      if (token === rangeToken.current) setErrors((s) => ({ ...s, [tab]: e.message || 'Error al cargar' }));
    } finally {
      if (token === rangeToken.current) setLoading((s) => ({ ...s, [tab]: false }));
    }
  }, [fetchTab]);

  // Cambio de rango → invalida caché y recarga la pestaña activa
  useEffect(() => {
    if (!range) return;
    rangeToken.current += 1;
    setCache({});
    setErrors({});
    loadTab(activeTab, range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const onTab = (tab) => {
    setActiveTab(tab);
    if (range && !cache[tab] && !loading[tab]) loadTab(tab, range);
  };

  const applyRange = (rng) => { if (rng?.from && rng?.to) setRange(rng); };

  // ── Estados globales ────────────────────────────────────────────────────────
  if (booting) {
    return <main className="adr"><div className="adr-state"><div className="adr-spinner" /><p>Cargando AndromedApp…</p></div></main>;
  }
  if (bootError) {
    return (
      <main className="adr"><div className="adr-state">
        <FaExclamationTriangle className="adr-state-icon" />
        <h3>No se pudo iniciar el módulo</h3><p>{bootError}</p>
        <button className="adr-btn primary" onClick={() => window.location.reload()}><FaSyncAlt /> Reintentar</button>
      </div></main>
    );
  }

  const data = cache[activeTab];
  const isLoading = !!loading[activeTab];
  const err = errors[activeTab];

  return (
    <main className="adr">
      {/* Header */}
      <header className="adr-header">
        <div className="adr-header-left">
          <span className="adr-title-icon"><FaCapsules /></span>
          <div>
            <h1 className="adr-title">AndromedApp</h1>
            <div className="adr-subtitle">
              Réplica CRM_FARMACIA · {range ? `${fecha(range.from)} – ${fecha(range.to)}` : '—'}
            </div>
          </div>
        </div>
        <div className="adr-header-actions">
          <button className="adr-btn" onClick={() => range && loadTab(activeTab, range)} disabled={isLoading}>
            <FaSyncAlt className={isLoading ? 'adr-spin' : ''} /> Actualizar
          </button>
        </div>
      </header>

      {/* Filtro de fecha global */}
      <DateFilter presets={presets} activePreset={activePreset} range={range} onApply={applyRange} />

      {/* Tabs */}
      <nav className="adr-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`adr-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => onTab(t.key)}>
            <span className="adr-tab-icon">{t.icon}</span>{t.label}
          </button>
        ))}
      </nav>

      {/* Contenido */}
      <section className="adr-content">
        {isLoading && <div className="adr-state small"><div className="adr-spinner" /><p>Cargando datos…</p></div>}
        {!isLoading && err && (
          <div className="adr-state small">
            <FaExclamationTriangle className="adr-state-icon" />
            <p>{err}</p>
            <button className="adr-btn" onClick={() => loadTab(activeTab, range)}><FaSyncAlt /> Reintentar</button>
          </div>
        )}
        {!isLoading && !err && data && (
          <>
            {activeTab === 'ventas' && <VentasTab data={data} range={range} />}
            {activeTab === 'pdv' && <PdvTab data={data} />}
            {activeTab === 'geo' && <GeoTab data={data} />}
            {activeTab === 'consejeras' && <ConsejerasTab data={data} />}
            {activeTab === 'productos' && <ProductosTab data={data} />}
          </>
        )}
      </section>
    </main>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Filtro de fecha
// ═══════════════════════════════════════════════════════════════════════════
function DateFilter({ presets, activePreset, range, onApply }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([range ? parseISO(range.from) : null, range ? parseISO(range.to) : null]);
  const [start, end] = sel;

  useEffect(() => { setSel([range ? parseISO(range.from) : null, range ? parseISO(range.to) : null]); }, [range]);

  const commit = (s, e) => { if (s && e) { onApply({ from: isoOf(s), to: isoOf(e) }); setOpen(false); } };

  return (
    <div className="adr-filter">
      <span className="adr-filter-label"><FaCalendarAlt /> Periodo</span>
      <div className="adr-presets">
        {presets.map((p) => (
          <button key={p.key} className={`adr-chip ${activePreset === p.key ? 'active' : ''}`} onClick={() => onApply(p.range)}>
            {p.label}
          </button>
        ))}
        <button className={`adr-chip ${activePreset === 'custom' ? 'active' : ''}`} onClick={() => setOpen((o) => !o)}>
          Personalizado
        </button>
      </div>
      {open && (
        <div className="adr-datepop">
          <DatePicker
            selected={start} startDate={start} endDate={end} onChange={([s, e]) => { setSel([s, e]); commit(s, e); }}
            selectsRange inline monthsShown={2} maxDate={parseISO(presets[presets.length - 1]?.range.to || isoOf(new Date()))}
          />
        </div>
      )}
    </div>
  );
}

// ── Componentes reutilizables ─────────────────────────────────────────────────
function DeltaBadge({ value }) {
  if (value == null) return null;
  const up = value >= 0;
  return <span className={`adr-delta ${up ? 'up' : 'down'}`}>{up ? '▲' : '▼'} {Math.abs(value).toFixed(1)}%</span>;
}

function Kpi({ icon, accent, label, value, delta, hint }) {
  return (
    <div className="adr-kpi" style={{ '--accent': accent || C.primary }}>
      <div className="adr-kpi-head"><span className="adr-kpi-label">{label}</span><span className="adr-kpi-icon">{icon}</span></div>
      <div className="adr-kpi-value">{value}</div>
      <div className="adr-kpi-foot">{delta !== undefined && <DeltaBadge value={delta} />}{hint && <span className="adr-kpi-hint">{hint}</span>}</div>
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="adr-tile" style={{ '--accent': accent || C.primary }}>
      <div className="adr-tile-value">{value}</div>
      <div className="adr-tile-label">{label}</div>
    </div>
  );
}

function Card({ title, icon, span, className = '', children }) {
  return (
    <section className={`adr-card ${span ? `adr-col-${span}` : ''} ${className}`}>
      {title && <h2 className="adr-card-title">{icon}{title}</h2>}
      {children}
    </section>
  );
}

function Empty({ text = 'Sin datos para el periodo.' }) { return <div className="adr-empty">{text}</div>; }

// Barras horizontales tipo ranking (lista con relleno proporcional)
function RankBars({ items, nameKey, valueKey, fmt = money, color = C.accent }) {
  const max = Math.max(1, ...items.map((i) => Number(i[valueKey]) || 0));
  if (!items.length) return <Empty />;
  return (
    <div className="adr-rank">
      {items.map((it, i) => {
        const v = Number(it[valueKey]) || 0;
        return (
          <div className="adr-rank-row" key={i}>
            <span className="adr-rank-name" title={it[nameKey]}>{it[nameKey] || '—'}</span>
            <div className="adr-rank-track"><div className="adr-rank-fill" style={{ width: `${(v / max) * 100}%`, background: color }} /></div>
            <span className="adr-rank-val">{fmt(v)}</span>
          </div>
        );
      })}
    </div>
  );
}

// Opciones base de chart.js
const axisMoney = { ticks: { callback: (v) => moneyC(v), font: { size: 10 }, maxTicksLimit: 5 }, grid: { color: '#eef2f6' } };
const baseLegend = { legend: { position: 'right', labels: { boxWidth: 10, font: { size: 11 }, padding: 8 } } };

// ═══════════════════════════════════════════════════════════════════════════
//  TAB · Ventas Totales
// ═══════════════════════════════════════════════════════════════════════════
function VentasTab({ data, range }) {
  const k = data.overview?.kpis || {};
  const d = data.overview?.deltas || {};
  const series = data.ts?.series || [];
  const weekday = data.ts?.weekday || [];
  const granularity = data.ts?.granularity || 'month';

  // ── Ventas por mes ─────────────────────────────────────────────────────────
  // Depende de `data.ts` (referencia estable del caché) y no de `series`, que es
  // un array nuevo en cada render y haría recalcular siempre.
  const monthly = useMemo(() => monthlySales(data.ts?.series, range), [data.ts, range]);
  const completos = monthly.filter((m) => !m.parcial);
  const hayParcial = monthly.length !== completos.length;
  const mejor = completos.reduce((a, b) => (a && a.valor >= b.valor ? a : b), null);
  const promedio = completos.length
    ? completos.reduce((s, m) => s + m.valor, 0) / completos.length : null;

  const mesData = {
    labels: monthly.map((m) => fmtMes(m.mes)),
    datasets: [{
      label: 'Ventas',
      data: monthly.map((m) => m.valor),
      // Un solo color de marca: la altura ya codifica la magnitud. La atenuación
      // NO es jerarquía visual, marca los meses recortados por el filtro.
      backgroundColor: monthly.map((m) => (m.parcial ? 'rgba(7,60,92,.32)' : C.primary)),
      borderRadius: 4, borderSkipped: 'bottom',
      categoryPercentage: 0.78, barPercentage: 0.92, maxBarThickness: 46,
    }],
  };
  const mesOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => fmtMesLargo(monthly[items[0].dataIndex].mes),
          label: (c) => `Ventas: ${money(c.parsed.y)}`,
          afterBody: (items) => {
            const i = items[0].dataIndex;
            const m = monthly[i];
            const prev = monthly[i - 1];
            const out = [`Unidades: ${num(m.unidades)}`, `Tickets: ${num(m.tickets)}`];
            // Sin delta si alguno de los dos meses está recortado: compararlos
            // pinta una caída que sólo existe por el corte del filtro.
            if (prev && !prev.parcial && !m.parcial && prev.valor > 0) {
              const v = ((m.valor - prev.valor) / prev.valor) * 100;
              out.push(`vs mes anterior: ${v >= 0 ? '▲' : '▼'} ${Math.abs(v).toFixed(1)}%`);
            }
            if (m.parcial) out.push('Mes parcial (recortado por el periodo)');
            return out;
          },
        },
      },
    },
    scales: {
      y: { beginAtZero: true, ...axisMoney },
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
    },
  };

  const lineData = {
    labels: series.map((s) => fmtBucket(s.bucket)),
    datasets: [{
      label: 'Ventas', data: series.map((s) => Number(s.valor) || 0),
      borderColor: C.primary, backgroundColor: 'rgba(7,60,92,.10)', fill: true,
      tension: 0.35, pointRadius: 0, borderWidth: 2,
    }],
  };
  const lineOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `Ventas: ${money(c.parsed.y)}` } } },
    scales: { y: { beginAtZero: true, ...axisMoney }, x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 10 } } },
  };

  const wdArr = WEEKDAYS.map((_, i) => weekday.find((w) => Number(w.dow) === i));
  const wdData = {
    labels: WEEKDAYS,
    datasets: [{ label: 'Ventas', data: wdArr.map((w) => Number(w?.valor) || 0), backgroundColor: C.accent, borderRadius: 6 }],
  };
  const wdOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => money(c.parsed.y) } } },
    scales: { y: { beginAtZero: true, ...axisMoney }, x: { grid: { display: false } } },
  };

  return (
    <>
      <div className="adr-kpis">
        <Kpi icon={<FaShoppingCart />} accent={C.primary} label="Ventas (valor)" value={money(k.valor)} delta={d.valor} hint={`${num(k.tickets)} tickets`} />
        <Kpi icon={<FaBoxes />} accent={C.accent} label="Unidades" value={num(k.unidades)} delta={d.unidades} />
        <Kpi icon={<FaReceipt />} accent="#3b82f6" label="Tickets" value={num(k.tickets)} delta={d.tickets} />
        <Kpi icon={<FaTag />} accent="#10b981" label="Ticket promedio" value={money(k.ticket_promedio)} hint={`${num(k.unidades_por_ticket)} u/ticket`} />
        <Kpi icon={<FaStore />} accent="#8b5cf6" label="PDV con venta" value={num(k.pdv_con_venta)} />
        <Kpi icon={<FaUserNurse />} accent="#ec4899" label="Consejeras activas" value={num(k.consejeras_con_venta)} />
      </div>

      <div className="adr-grid">
        <Card title="Ventas por mes" icon={<FaChartBar />} span={12}>
          <div className="adr-chart">{monthly.length ? <Bar data={mesData} options={mesOpts} /> : <Empty />}</div>
          {monthly.length > 0 && (
            <p className="adr-note">
              Agrupado por mes calendario dentro del periodo seleccionado.
              {hayParcial && ' Los meses recortados por el filtro se dibujan atenuados y quedan fuera del promedio y de la comparación mes a mes.'}
              {mejor && mejor.valor > 0 && ` · Mejor mes: ${fmtMesLargo(mejor.mes)} (${money(mejor.valor)}).`}
              {promedio != null && ` · Promedio mensual: ${money(promedio)}.`}
            </p>
          )}
        </Card>

        {/* Con rango > 92 días el backend ya devuelve buckets mensuales: la línea
            sería la misma serie que las barras de arriba, así que sólo se pinta
            cuando aporta el detalle diario. */}
        {granularity === 'day' && (
          <Card title="Tendencia diaria de ventas" icon={<FaChartLine />} span={12}>
            <div className="adr-chart">{series.length ? <Line data={lineData} options={lineOpts} /> : <Empty />}</div>
          </Card>
        )}

        <Card title="Composición (subtotal vs IVA)" icon={<FaLayerGroup />} span={4}>
          <div className="adr-chart sm">
            <Doughnut
              data={{ labels: ['Subtotal', 'IVA'], datasets: [{ data: [Number(k.subtotal) || 0, Number(k.iva) || 0], backgroundColor: [C.primary, C.accent], borderColor: '#fff', borderWidth: 2 }] }}
              options={{ responsive: true, maintainAspectRatio: false, cutout: '62%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } }, tooltip: { callbacks: { label: (c) => `${c.label}: ${money(c.parsed)}` } } } }}
            />
          </div>
        </Card>

        <Card title="Ventas por día de la semana" icon={<FaCalendarAlt />} span={8}>
          <div className="adr-chart sm">{weekday.length ? <Bar data={wdData} options={wdOpts} /> : <Empty />}</div>
        </Card>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TAB · PDV
// ═══════════════════════════════════════════════════════════════════════════
function PdvTab({ data }) {
  const s = data.summary || {};
  const top = data.top || [];
  const byTipo = data.by_tipo || [];

  const tipoData = {
    labels: byTipo.map((t) => t.tipo),
    datasets: [{ data: byTipo.map((t) => Number(t.valor) || 0), backgroundColor: C.series, borderColor: '#fff', borderWidth: 2 }],
  };

  return (
    <>
      <div className="adr-tiles">
        <Tile label="PDV en catálogo" value={num(s.total_pdv)} accent={C.primary} />
        <Tile label="PDV con venta" value={num(s.pdv_con_venta)} accent="#10b981" />
        <Tile label="Cobertura" value={pct1(s.cobertura)} accent={C.accent} />
        <Tile label="PDV sin venta" value={num(s.pdv_sin_venta)} accent="#ef4444" />
        <Tile label="Cadenas" value={num(s.cadenas)} accent="#3b82f6" />
        <Tile label="Estados" value={num(s.estados)} accent="#8b5cf6" />
      </div>

      <div className="adr-grid">
        <Card title="Top puntos de venta" icon={<FaStore />} span={8}>
          <RankBars items={top.map((p) => ({ ...p, etiqueta: `${p.pdv}${p.cadena ? ' · ' + p.cadena : ''}` }))} nameKey="etiqueta" valueKey="valor" />
        </Card>
        <Card title="Ventas por tipo de PDV" icon={<FaLayerGroup />} span={4}>
          <div className="adr-chart sm">{byTipo.length ? <Doughnut data={tipoData} options={{ responsive: true, maintainAspectRatio: false, cutout: '58%', plugins: { ...baseLegend, tooltip: { callbacks: { label: (c) => `${c.label}: ${money(c.parsed)}` } } } }} /> : <Empty />}</div>
        </Card>

        <Card title="Detalle de PDV" icon={<FaStore />} span={12}>
          <div className="adr-table-wrap">
            <table className="adr-table">
              <thead><tr><th>PDV</th><th>Cadena</th><th>Estado</th><th>Tipo</th><th className="r">Ventas</th><th className="r">Unid.</th><th className="r">Tickets</th></tr></thead>
              <tbody>
                {top.map((p) => (
                  <tr key={p.pdv_id}>
                    <td>{p.pdv}</td><td>{p.cadena}</td><td>{p.estado || '—'}</td><td>{p.tipo || '—'}</td>
                    <td className="r strong">{money(p.valor)}</td><td className="r">{num(p.unidades)}</td><td className="r">{num(p.tickets)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TAB · Cadenas / Geolocalización
// ═══════════════════════════════════════════════════════════════════════════
function GeoTab({ data }) {
  const byChain = data.by_chain || [];
  const byEstado = data.by_estado || [];
  const checkins = data.checkins || [];
  const maxN = Math.max(1, ...checkins.map((c) => Number(c.n) || 0));

  const chainData = {
    labels: byChain.map((c) => c.cadena),
    datasets: [{ label: 'Ventas', data: byChain.map((c) => Number(c.valor) || 0), backgroundColor: C.primary, borderRadius: 5 }],
  };
  const chainOpts = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => money(c.parsed.x) } } },
    scales: { x: { beginAtZero: true, ...axisMoney }, y: { grid: { display: false }, ticks: { font: { size: 10 } } } },
  };

  return (
    <div className="adr-grid">
      <Card title="Ventas por cadena" icon={<FaLayerGroup />} span={6}>
        <div className="adr-chart tall">{byChain.length ? <Bar data={chainData} options={chainOpts} /> : <Empty />}</div>
      </Card>

      <Card title="Ventas por estado" icon={<FaMapMarkerAlt />} span={6}>
        <RankBars items={byEstado} nameKey="estado" valueKey="valor" color={C.primary} />
      </Card>

      <Card title="Mapa de check-ins (geolocalización de visitas)" icon={<FaMapMarkerAlt />} span={12} className="adr-map-card">
        {checkins.length ? (
          <div className="adr-map">
            <MapContainer center={[23.6, -102.5]} zoom={5} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {checkins.map((c, i) => {
                const n = Number(c.n) || 0;
                return (
                  <CircleMarker key={i} center={[Number(c.lat), Number(c.lng)]}
                    radius={3 + Math.log(n + 1) * 2.2}
                    pathOptions={{ color: C.accent, weight: 1, fillColor: C.accent, fillOpacity: 0.25 + 0.5 * (n / maxN) }}>
                    <Popup>{num(n)} check-ins<br />{c.lat}, {c.lng}</Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        ) : <Empty text="Sin check-ins geolocalizados para el periodo." />}
        <p className="adr-note">Puntos agregados por coordenada (redondeo ~1 km), acotados al territorio nacional. Tamaño y opacidad según volumen de visitas.</p>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TAB · Consejera
// ═══════════════════════════════════════════════════════════════════════════
function ConsejerasTab({ data }) {
  const detail = data.detail || [];
  const top = detail.slice(0, 10);

  const barData = {
    labels: top.map((c) => c.nombre),
    datasets: [{ label: 'Ventas', data: top.map((c) => Number(c.valor) || 0), backgroundColor: C.accent, borderRadius: 5 }],
  };
  const barOpts = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => money(c.parsed.x) } } },
    scales: { x: { beginAtZero: true, ...axisMoney }, y: { grid: { display: false }, ticks: { font: { size: 10 } } } },
  };

  return (
    <>
      <div className="adr-tiles">
        <Tile label="Consejeras activas" value={num(data.total_activas)} accent={C.primary} />
        <Tile label="Con ventas en el periodo" value={num(data.con_actividad)} accent="#10b981" />
        <Tile label="En ranking" value={num(detail.length)} accent={C.accent} />
      </div>

      <div className="adr-grid">
        <Card title="Top consejeras por ventas" icon={<FaUserNurse />} span={12}>
          <div className="adr-chart tall">{top.length ? <Bar data={barData} options={barOpts} /> : <Empty />}</div>
        </Card>

        <Card title="Desempeño por consejera" icon={<FaUserNurse />} span={12}>
          <div className="adr-table-wrap">
            <table className="adr-table">
              <thead><tr>
                <th>Consejera</th><th className="r">Ventas</th><th className="r">Unid.</th><th className="r">Tickets</th>
                <th className="r"><FaMapMarkerAlt title="Visitas / check-ins" /></th>
                <th className="r"><FaClock title="Horas laboradas" /></th>
                <th className="r"><FaBolt title="Venta por hora" /></th>
                <th className="r"><FaFileMedical title="Recetas" /></th>
                <th className="r"><FaCheckCircle title="Evaluaciones" /></th>
              </tr></thead>
              <tbody>
                {detail.map((c) => (
                  <tr key={c.user_id}>
                    <td>{c.nombre || '—'}</td>
                    <td className="r strong">{money(c.valor)}</td>
                    <td className="r">{num(c.unidades)}</td>
                    <td className="r">{num(c.tickets)}</td>
                    <td className="r">{num(c.visitas)}</td>
                    <td className="r">{num(c.horas)}</td>
                    <td className="r">{c.venta_por_hora != null ? money(c.venta_por_hora) : '—'}</td>
                    <td className="r">{num(c.recetas)}</td>
                    <td className="r">{num(c.evaluaciones)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="adr-note">Visitas = check-ins registrados · Venta/hora = ventas ÷ horas laboradas en el periodo.</p>
        </Card>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TAB · Producto
// ═══════════════════════════════════════════════════════════════════════════
function ProductosTab({ data }) {
  const s = data.summary || {};
  const top = data.top || [];

  return (
    <>
      <div className="adr-tiles">
        <Tile label="SKU vendidos" value={num(s.skus)} accent={C.primary} />
        <Tile label="Unidades" value={num(s.unidades)} accent={C.accent} />
        <Tile label="Valor total" value={money(s.valor)} accent="#10b981" />
      </div>

      <div className="adr-grid">
        <Card title="Top productos por valor" icon={<FaTag />} span={7}>
          {/* etiqueta = nombre del catálogo; cae al código en los SKU que no cubre (11 de 222) */}
          <RankBars items={top.map((p) => ({ ...p, etiqueta: p.item_name || p.item_code }))}
                    nameKey="etiqueta" valueKey="valor" color={C.accent} />
        </Card>
        <Card title="Detalle de productos" icon={<FaTag />} span={5}>
          <div className="adr-table-wrap">
            <table className="adr-table">
              <thead><tr><th>Producto</th><th>Marca</th><th className="r">Valor</th><th className="r">Unid.</th><th className="r">Tickets</th></tr></thead>
              <tbody>
                {top.map((p, i) => (
                  <tr key={i}>
                    <td>
                      {p.item_name || p.item_code || '—'}
                      {p.es_booster && <span className="adr-badge booster">booster</span>}
                      {p.item_name && <div className="adr-sub">{p.item_code}</div>}
                    </td>
                    <td>{p.marca || '—'}</td>
                    <td className="r strong">{money(p.valor)}</td><td className="r">{num(p.unidades)}</td><td className="r">{num(p.tickets)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}
